<template>
  <div class="particle-container">
    <div ref="sceneRef" class="scene">
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import Matter from 'matter-js';

// Props
const props = defineProps({
  percentComplete: {
    type: Number,
    default: 0,
    validator: (value) => value >= 0 && value <= 100
  },
  autoStart: {
    type: Boolean,
    default: false
  }
});

// Configuration constants
const CONFIG = {
  PARTICLE_SIZE: 28,
  INITIAL_PARTICLE_COUNT: 100,
  SPAWN_DELAY_MS: 30,
  CLICK_SPAWN_COUNT: 5,
  CLICK_SPAWN_DELAY_MS: 40,
  MAX_PARTICLES: 300,
  GRAVITY: 1.2,
  RESTITUTION: 0.5,
  FRICTION: 0.3,
  FRICTION_AIR: 0.01,
  SLEEP_THRESHOLD: 60,
  VELOCITY_X_RANGE: 8,
  VELOCITY_Y_MAX: 4,
  SIZE_VARIATION_MIN: 0.8,
  SIZE_VARIATION_MAX: 1.6,
  FONT_SIZE_RATIO: 0.72,
  WALL_THICKNESS: 50,
  WALL_OFFSET: 25,
  MOUSE_STIFFNESS: 0.2,
};

const ICONS = [
  '\uf000', '\uf001', '\uf004', '\uf005', '\uf007', '\uf008', '\uf00b', '\uf00c',
  '\uf013', '\uf015', '\uf017', '\uf019', '\uf01c', '\uf021', '\uf025', '\uf026',
  '\uf028', '\uf02b', '\uf030', '\uf03a', '\uf03d', '\uf043', '\uf048', '\uf04b',
  '\uf051', '\uf055', '\uf057', '\uf059', '\uf05a', '\uf06b', '\uf06e', '\uf070',
  '\uf072', '\uf075', '\uf077', '\uf078', '\uf07a', '\uf07b', '\uf080', '\uf084',
  '\uf085', '\uf091', '\uf09d', '\uf0a0', '\uf0ac', '\uf0b0', '\uf0c3', '\uf0c8',
  '\uf0e0', '\uf0f3',
];

const COLORS = [
  '#ff6b6b', '#ffa94d', '#ffd43b', '#69db7c', '#4dabf7',
  '#748ffc', '#da77f2', '#f783ac', '#63e6be', '#74c0fc',
];

// Component-scoped state
const sceneRef = ref(null);
const canvasDimensions = ref({ width: 500, height: 500 });
let engine = null;
let render = null;
let runner = null;
let mouseConstraint = null;
let textureCache = {};
let timeouts = [];
let particleCount = 0;
let clickHandler = null;

// Computed property: maximum particles that can fit in canvas
const maxParticlesFit = computed(() => {
  const { width, height } = canvasDimensions.value;
  const canvasArea = width * height;

  // Average particle size (accounting for variation)
  const avgSize = CONFIG.PARTICLE_SIZE * ((CONFIG.SIZE_VARIATION_MIN + CONFIG.SIZE_VARIATION_MAX) / 2);
  const particleArea = Math.PI * Math.pow(avgSize / 2, 2);

  // Packing efficiency for circles is approximately 0.9069 (hexagonal packing)
  // Use 0.9069 for more realistic spacing with physics simulation
  const packingEfficiency = 0.9069;

  return Math.floor((canvasArea * packingEfficiency) / particleArea);
});

// Computed property: target number of particles based on completion percentage
const targetParticles = computed(() => {
  return Math.floor(maxParticlesFit.value * (props.percentComplete / 100));
});

// Watch for changes in targetParticles and spawn more if needed
watch(targetParticles, (newTarget, oldTarget) => {
  if (!props.autoStart || !engine) return;

  const particlesToAdd = newTarget - particleCount;
  if (particlesToAdd > 0) {
    const { width, height } = canvasDimensions.value;

    for (let i = 0; i < particlesToAdd; i++) {
      const timeoutId = setTimeout(() => {
        spawnParticle(
          randomInRange(5, width - 5),
          randomInRange(-10, -20) // Spawn above canvas, scaled to canvas height
        );
      }, i * CONFIG.SPAWN_DELAY_MS);

      timeouts.push(timeoutId);
    }
  }
});

// Utility functions
const randomChoice = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randomInRange = (min, max) => min + Math.random() * (max - min);

function makeIconTexture(char, color, size) {
  try {
    const pixelRatio = window.devicePixelRatio || 1;
    const scaledSize = size * pixelRatio;
    
    const canvas = document.createElement('canvas');
    canvas.width = scaledSize;
    canvas.height = scaledSize;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Failed to get canvas context');
      return null;
    }

    ctx.font = `900 ${scaledSize * CONFIG.FONT_SIZE_RATIO}px "Font Awesome 6 Free"`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(char, scaledSize / 2, scaledSize / 2);
    return canvas.toDataURL();
  } catch (error) {
    console.error('Error creating texture:', error);
    return null;
  }
}

function getTexture(char, color, size) {
  const key = `${char}_${color}_${size}`;
  if (!textureCache[key]) {
    const texture = makeIconTexture(char, color, size);
    if (texture) {
      textureCache[key] = texture;
    }
  }
  return textureCache[key];
}

function spawnParticle(x, y) {
  if (particleCount >= targetParticles.value) {
    return;
  }

  if (!engine) {
    console.error('Engine not initialized');
    return;
  }

  try {
    const { Bodies, Body, World } = Matter;
    const char = randomChoice(ICONS);
    const color = randomChoice(COLORS);
    const size = CONFIG.PARTICLE_SIZE * randomInRange(
      CONFIG.SIZE_VARIATION_MIN,
      CONFIG.SIZE_VARIATION_MAX
    );

    const texture = getTexture(char, color, size);
    if (!texture) {
      return;
    }

    const pixelRatio = window.devicePixelRatio || 1;

    const body = Bodies.circle(x, y, size / 2, {
      restitution: CONFIG.RESTITUTION,
      friction: CONFIG.FRICTION,
      frictionAir: CONFIG.FRICTION_AIR,
      sleepThreshold: CONFIG.SLEEP_THRESHOLD,
      render: {
        sprite: {
          texture,
          xScale: 1 / pixelRatio,
          yScale: 1 / pixelRatio,
        },
      },
    });

    Body.setVelocity(body, {
      x: (Math.random() - 0.5) * CONFIG.VELOCITY_X_RANGE,
      y: -Math.random() * CONFIG.VELOCITY_Y_MAX,
    });

    World.add(engine.world, body);
    particleCount++;
  } catch (error) {
    console.error('Error spawning particle:', error);
  }
}

function createWalls() {
  const { Bodies, World } = Matter;
  const { width, height } = canvasDimensions.value;
  const wallOpts = { isStatic: true, render: { fillStyle: 'transparent' } };

  World.add(engine.world, [
    Bodies.rectangle(width / 2, height + CONFIG.WALL_OFFSET, width, CONFIG.WALL_THICKNESS, wallOpts),
    Bodies.rectangle(-CONFIG.WALL_OFFSET, height / 2, CONFIG.WALL_THICKNESS, height, wallOpts),
    Bodies.rectangle(width + CONFIG.WALL_OFFSET, height / 2, CONFIG.WALL_THICKNESS, height, wallOpts),
    Bodies.rectangle(width / 2, -CONFIG.WALL_OFFSET, width, CONFIG.WALL_THICKNESS, wallOpts),
  ]);
}

function spawnInitialParticles() {
  const { width, height } = canvasDimensions.value;

  for (let i = 0; i < targetParticles.value; i++) {
    const timeoutId = setTimeout(() => {
      spawnParticle(
        randomInRange(5, width - 5),
        randomInRange(-10, -20) // Spawn above canvas, scaled to canvas height
      );
    }, i * CONFIG.SPAWN_DELAY_MS);

    timeouts.push(timeoutId);
  }
}

function setupClickHandler() {
  if (!render?.canvas) return;

  let lastClickTime = 0;
  const THROTTLE_MS = 100;

  clickHandler = (e) => {
    const now = Date.now();
    if (now - lastClickTime < THROTTLE_MS) return;
    lastClickTime = now;

    const rect = render.canvas.getBoundingClientRect();

    for (let i = 0; i < CONFIG.CLICK_SPAWN_COUNT; i++) {
      const timeoutId = setTimeout(() => {
        spawnParticle(
          e.clientX - rect.left + randomInRange(-40, 40),
          e.clientY - rect.top + randomInRange(-40, 40)
        );
      }, i * CONFIG.CLICK_SPAWN_DELAY_MS);

      timeouts.push(timeoutId);
    }
  };

  render.canvas.addEventListener('click', clickHandler);
}

function setupMouseConstraint() {
  const { Mouse, MouseConstraint, World } = Matter;

  const mouse = Mouse.create(render.canvas);
  mouseConstraint = MouseConstraint.create(engine, {
    mouse,
    constraint: {
      stiffness: CONFIG.MOUSE_STIFFNESS,
      render: { visible: false }
    },
  });

  World.add(engine.world, mouseConstraint);
  render.mouse = mouse;
}

function initMatter() {
  try {
    if (!sceneRef.value) {
      console.error('Scene ref not available');
      return;
    }

    const { Engine, Render, Runner } = Matter;

    // Get container dimensions
    const rect = sceneRef.value.getBoundingClientRect();
    canvasDimensions.value = { width: rect.width, height: rect.height };

    console.log('Canvas dimensions:', canvasDimensions.value);
    console.log('Target particles:', targetParticles.value);
    console.log('Max particles fit:', maxParticlesFit.value);
    console.log('Percent complete:', props.percentComplete);

    // Create engine
    engine = Engine.create({
      gravity: { y: CONFIG.GRAVITY },
      enableSleeping: true
    });

    // Create renderer
    render = Render.create({
      element: sceneRef.value,
      engine: engine,
      options: {
        width: canvasDimensions.value.width,
        height: canvasDimensions.value.height,
        background: '#1a1a2e',
        wireframes: false,
        pixelRatio: 'auto',
        showSleeping: false
      },
    });

    Render.run(render);
    runner = Runner.create();
    Runner.run(runner, engine);

    createWalls();
    spawnInitialParticles();
    setupClickHandler();
    setupMouseConstraint();
  } catch (error) {
    console.error('Error initializing Matter.js:', error);
  }
}

onMounted(async () => {
  await nextTick();
  initMatter();
});

onBeforeUnmount(() => {
  try {
    // Clear all timeouts
    timeouts.forEach(id => clearTimeout(id));
    timeouts = [];

    // Remove event listeners
    if (clickHandler && render?.canvas) {
      render.canvas.removeEventListener('click', clickHandler);
      clickHandler = null;
    }

    // Clear texture cache
    textureCache = {};

    const { World, Render, Runner, Engine } = Matter;

    if (mouseConstraint && engine) {
      World.remove(engine.world, mouseConstraint);
      mouseConstraint = null;
    }

    if (render) {
      Render.stop(render);
      if (render.canvas) {
        render.canvas.remove();
      }
      render.canvas = null;
      render.context = null;
      render.textures = {};
      render = null;
    }

    if (runner) {
      Runner.stop(runner);
      runner = null;
    }

    if (engine) {
      World.clear(engine.world, false);
      Engine.clear(engine);
      engine = null;
    }

    particleCount = 0;
  } catch (error) {
    console.error('Error during cleanup:', error);
  }
});
</script>

<style scoped>
.particle-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.scene {
  width: 100%;
  max-width: 500px;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

.scene :deep(canvas) {
  display: block;
}

.hint {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.3);
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
}
</style>
